import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { AdvancedSearchAttachment, AdvancedSearchDraft } from 'src/app/models/advanced-search';
import { UtilityFunctionsService } from 'src/app/shared/providers/utility-functions.service';

@Component({
  selector: 'app-advanced-search-navbar',
  templateUrl: './advanced-search-navbar.component.html',
  styleUrls: ['./advanced-search-navbar.component.scss']
})
export class AdvancedSearchNavbarComponent implements AfterViewInit, OnDestroy {
  @Input() libraryButtonMode: 'add' | 'back' | 'close' = 'add';
  @Input() maxAttachments: number = 10;
  @Input() isHidden: boolean = false;
  @Input() attachmentsEnabled: boolean = true;
  @Input() isBusy: boolean = false;
  @Input() hideAccountButton: boolean = false;
  @Input() devButtonVisible: boolean = false;
  @Input() saveCollectionActionVisible: boolean = false;
  @Input() saveCollectionActionDisabled: boolean = false;
  @Input() pairBatchProgress: { done: number; total: number } | null = null;
  // Overrides the prompt placeholder while no attachment is staged (the landing says
  // "Search references"; every other host keeps the default).
  @Input() placeholder: string | null = null;
  // Paints a dark plate behind the collapsed row so the dock stays readable over light
  // content (the landing floats it over its white section). Off everywhere else.
  @Input() @HostBinding('class.has-plate') plate: boolean = false;
  // The collapsed submit circle only appears once there is something to submit (Figma's dock
  // has no arrow at rest, and on phones the resting placeholder needs that room). Off elsewhere.
  @Input() submitOnlyWithDraft: boolean = false;

  @Output() libraryClicked = new EventEmitter<MouseEvent | undefined>();
  @Output() accountClicked = new EventEmitter<void>();
  @Output() devClicked = new EventEmitter<void>();
  @Output() submitRequested = new EventEmitter<AdvancedSearchDraft>();
  @Output() saveCollectionRequested = new EventEmitter<void>();
  @Output() cancelRequested = new EventEmitter<void>();
  @Output() draftChange = new EventEmitter<AdvancedSearchDraft>();

  // = `--advanced-search-collapsed-row-width` default in the SCSS (600 prompt + 2×52 slots + 60).
  private static readonly COLLAPSED_ROW_MAX_WIDTH = 764;

  @ViewChild('fileInput', { static: false })
  public fileInputRef?: ElementRef<HTMLInputElement>;

  @ViewChild('promptTextarea', { static: false })
  public promptTextareaRef?: ElementRef<HTMLTextAreaElement>;

  @ViewChild('collapsedPromptInput', { static: false })
  public collapsedPromptInputRef?: ElementRef<HTMLInputElement>;

  @ViewChild('advancedSearchShell', { static: false })
  public advancedSearchShellRef?: ElementRef<HTMLElement>;

  @ViewChild('collapsedRow', { static: false })
  public collapsedRowRef?: ElementRef<HTMLElement>;

  @ViewChild('collapsedStart', { static: false })
  public collapsedStartRef?: ElementRef<HTMLElement>;

  @ViewChild('collapsedEnd', { static: false })
  public collapsedEndRef?: ElementRef<HTMLElement>;

  @ViewChild('expandedRow', { static: false })
  public expandedRowRef?: ElementRef<HTMLElement>;

  @ViewChild('expandedStart', { static: false })
  public expandedStartRef?: ElementRef<HTMLElement>;

  @ViewChild('expandedEnd', { static: false })
  public expandedEndRef?: ElementRef<HTMLElement>;

  public isExpanded: boolean = false;
  public prompt: string = '';
  public attachments: AdvancedSearchAttachment[] = [];

  private resizeObserver?: ResizeObserver;
  private recalculateFooterShiftFrame?: number;
  private lastCollapsedSideSlotWidth: number = 52;
  private lastExpandedStartWidth: number = 36;
  private lastExpandedEndWidth: number = 108;

  constructor(private utilityService: UtilityFunctionsService) {}

  ngAfterViewInit(): void {
    this.resizeTextarea();
    this.initFooterShiftObserver();
    this.scheduleFooterShiftRecalculation();
  }

  public get expandedPlaceholder(): string {
    return this.getPromptPlaceholder();
  }

  public get hasDraft(): boolean {
    return this.prompt.trim().length > 0 || (this.attachmentsEnabled && this.attachments.length > 0);
  }

  public get canSubmit(): boolean {
    return !this.isBusy && this.hasDraft;
  }

  public get collapsedSubmitVisible(): boolean {
    return !this.submitOnlyWithDraft || this.hasDraft || this.isBusy || this.shouldShowSaveCollectionAction;
  }

  public get primaryActionLabel(): string {
    if (this.isBusy) {
      return 'Cancel';
    }

    return this.shouldShowSaveCollectionAction ? 'Save Collection' : 'Render';
  }

  public get shouldShowSaveCollectionAction(): boolean {
    return !this.isBusy && this.saveCollectionActionVisible;
  }

  public get pairBatchProgressPct(): number {
    if (!this.pairBatchProgress?.total) return 0;
    return Math.round((this.pairBatchProgress.done / this.pairBatchProgress.total) * 100);
  }

  public get isPrimaryActionDisabled(): boolean {
    if (this.isBusy) {
      return false;
    }

    if (this.shouldShowSaveCollectionAction) {
      return this.saveCollectionActionDisabled;
    }

    return !this.hasDraft;
  }

  public get canAddMoreAttachments(): boolean {
    if (!this.attachmentsEnabled) {
      return false;
    }

    return this.attachments.length < this.maxAttachments;
  }

  public get isLibraryButtonInBackMode(): boolean {
    return this.libraryButtonMode !== 'add';
  }

  public get collapsedLibraryButtonAriaLabel(): string {
    if (this.libraryButtonMode === 'back') {
      return 'Back';
    }

    if (this.libraryButtonMode === 'close') {
      return 'Close';
    }

    return 'Library';
  }

  public onLibraryClick(event?: MouseEvent): void {
    event?.stopPropagation();
    this.libraryClicked.emit(event);
  }

  public onAccountClick(event?: Event): void {
    event?.stopPropagation();
    this.accountClicked.emit();
  }

  public onDevClick(event?: Event): void {
    event?.stopPropagation();
    this.devClicked.emit();
  }

  public onBackClick(event?: Event): void {
    event?.stopPropagation();
    if (this.isBusy) {
      return;
    }
    this.collapseComposer();
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscapeKeydown(event: KeyboardEvent): void {
    if (!this.isExpanded) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.onBackClick();
  }

  public onCollapsedComposerClick(event?: Event): void {
    event?.stopPropagation();
    if (this.isBusy) {
      return;
    }
    this.focusCollapsedPromptInput();
  }

  public onCollapsedPromptInput(): void {
    this.emitDraftChange();
  }

  public onCollapsedPromptKeydown(event: KeyboardEvent): void {
    if (this.isBusy) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    // Enter always runs a search, never the Save Collection action — so the user
    // can keep searching while results are selected.
    this.submitDraft(true);
  }

  public onPromptInput(): void {
    this.resizeTextarea();
    this.emitDraftChange();
  }

  public onAddImageClick(event: Event): void {
    event.stopPropagation();

    if (this.isBusy) {
      return;
    }

    if (!this.attachmentsEnabled) {
      return;
    }

    if (!this.canAddMoreAttachments) {
      this.utilityService.showSnackBar(`You can attach up to ${this.maxAttachments} images.`, 0);
      return;
    }

    this.fileInputRef?.nativeElement.click();
  }

  public onPlusRipplePointerDown(event: PointerEvent): void {
    if (typeof window === 'undefined') {
      return;
    }

    const buttonElement = event.currentTarget as HTMLElement | null;
    if (!buttonElement) {
      return;
    }

    const activeTimeoutId = buttonElement.dataset['rippleTimeoutId'];
    if (activeTimeoutId) {
      window.clearTimeout(Number(activeTimeoutId));
    }

    buttonElement.classList.remove('is-ripple-active');
    void buttonElement.offsetWidth;
    buttonElement.classList.add('is-ripple-active');

    const timeoutId = window.setTimeout(() => {
      buttonElement.classList.remove('is-ripple-active');
      delete buttonElement.dataset['rippleTimeoutId'];
    }, 420);

    buttonElement.dataset['rippleTimeoutId'] = `${timeoutId}`;
  }

  public onFileInputChange(event: Event): void {
    if (!this.attachmentsEnabled) {
      this.resetFileInput();
      return;
    }

    const inputElement = event.target as HTMLInputElement;
    const selectedFiles = Array.from(inputElement.files || []);

    const appendedFilesCount = this.appendSelectedFiles(selectedFiles);
    this.resetFileInput();

    if (appendedFilesCount > 0) {
      this.expandComposer();
    }
  }

  public onAttachmentRemove(attachment: AdvancedSearchAttachment, event: Event): void {
    event.stopPropagation();
    if (this.isBusy) {
      return;
    }

    URL.revokeObjectURL(attachment.previewUrl);
    this.attachments = this.attachments.filter((item) => item.id !== attachment.id);
    this.emitDraftChange();

    if (this.attachments.length === 0) {
      this.collapseComposer();
    }
  }

  public onPrimaryActionClick(event: Event): void {
    event.stopPropagation();
    if (this.isBusy) {
      this.cancelDraftRequest();
      return;
    }

    this.submitDraft();
  }

  public clearDraft(): void {
    this.resetDraft();
    this.emitDraftChange();
    this.collapseComposer();
  }

  public setPrompt(prompt: string): void {
    this.prompt = prompt || '';
    this.emitDraftChange();
    this.resizeTextarea();
  }

  private submitDraft(forceSearch: boolean = false): void {
    if (!forceSearch && this.shouldShowSaveCollectionAction) {
      if (!this.saveCollectionActionDisabled) {
        this.saveCollectionRequested.emit();
      }
      return;
    }

    if (!this.canSubmit) {
      return;
    }

    this.submitRequested.emit(this.buildDraft());
    this.emitDraftChange();
  }

  private cancelDraftRequest(): void {
    this.resetDraft();
    this.cancelRequested.emit();
    this.emitDraftChange();
    this.collapseComposer();
  }

  public trackByAttachmentId(index: number, attachment: AdvancedSearchAttachment): string {
    return attachment.id;
  }

  private expandComposer(): void {
    if (!this.isExpanded) {
      this.isExpanded = true;
    }

    setTimeout(() => {
      this.connectFooterShiftObserver();
      this.scheduleFooterShiftRecalculation();
      this.focusTextarea();
      this.resizeTextarea();
    });
  }

  private collapseComposer(): void {
    if (!this.isExpanded) {
      return;
    }

    this.isExpanded = false;
    this.queueLayoutRecalculation();
  }

  public recalculateFooterShift(): void {
    const shellElement = this.advancedSearchShellRef?.nativeElement;
    if (!shellElement) {
      return;
    }

    this.lastCollapsedSideSlotWidth = this.measureSideSlotWidth(
      this.collapsedStartRef?.nativeElement,
      this.collapsedEndRef?.nativeElement,
      this.lastCollapsedSideSlotWidth
    );

    shellElement.style.setProperty('--advanced-search-collapsed-side-slot-width', `${this.lastCollapsedSideSlotWidth}px`);

    const shellWidth = shellElement.getBoundingClientRect().width;
    // Size the row from the shell's content box, never from the row itself: the row's width is
    // `min(--advanced-search-collapsed-row-width, 100%)`, i.e. the value written below, so measuring
    // it fed the last result back in — once the window had been narrow the dock stayed narrow.
    const shellStyles = window.getComputedStyle(shellElement);
    const shellContentWidth = shellElement.clientWidth
      - parseFloat(shellStyles.paddingLeft || '0')
      - parseFloat(shellStyles.paddingRight || '0');
    const collapsedRowWidth = Math.min(shellContentWidth || shellWidth, AdvancedSearchNavbarComponent.COLLAPSED_ROW_MAX_WIDTH);
    const collapsedAvailableWidth = this.calculateAvailableMainWidth(
      this.collapsedRowRef?.nativeElement,
      collapsedRowWidth,
      this.lastCollapsedSideSlotWidth,
      40,
      10
    );

    const collapsedMainWidth = Math.max(180, Math.min(600, collapsedAvailableWidth));
    const collapsedRowWidthTarget = Math.ceil(collapsedMainWidth + (this.lastCollapsedSideSlotWidth * 2) + 60);

    shellElement.style.setProperty('--advanced-search-collapsed-main-width', `${Math.floor(collapsedMainWidth)}px`);
    shellElement.style.setProperty('--advanced-search-collapsed-row-width', `${collapsedRowWidthTarget}px`);
    this.recalculateExpandedComposerGeometry(shellElement, shellWidth);
  }

  private focusTextarea(): void {
    this.promptTextareaRef?.nativeElement.focus();
  }

  private focusCollapsedPromptInput(): void {
    this.collapsedPromptInputRef?.nativeElement.focus();
  }

  private resizeTextarea(): void {
    const textarea = this.promptTextareaRef?.nativeElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = '0px';
    const nextHeight = Math.min(textarea.scrollHeight, 160);
    textarea.style.height = `${nextHeight}px`;
  }

  private initFooterShiftObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleFooterShiftRecalculation();
    });

    this.connectFooterShiftObserver();
  }

  private connectFooterShiftObserver(): void {
    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();

    this.observeFooterShiftElement(this.advancedSearchShellRef?.nativeElement);
    this.observeFooterShiftElement(this.collapsedRowRef?.nativeElement);
    this.observeFooterShiftElement(this.collapsedStartRef?.nativeElement);
    this.observeFooterShiftElement(this.collapsedEndRef?.nativeElement);
    this.observeFooterShiftElement(this.expandedRowRef?.nativeElement);
    this.observeFooterShiftElement(this.expandedStartRef?.nativeElement);
    this.observeFooterShiftElement(this.expandedEndRef?.nativeElement);
  }

  private observeFooterShiftElement(element?: HTMLElement): void {
    if (element && this.resizeObserver) {
      this.resizeObserver.observe(element);
    }
  }

  private queueLayoutRecalculation(): void {
    setTimeout(() => {
      this.connectFooterShiftObserver();
      this.scheduleFooterShiftRecalculation();
    });
  }

  private scheduleFooterShiftRecalculation(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.recalculateFooterShiftFrame) {
      window.cancelAnimationFrame(this.recalculateFooterShiftFrame);
    }

    this.recalculateFooterShiftFrame = window.requestAnimationFrame(() => {
      this.recalculateFooterShiftFrame = undefined;
      this.recalculateFooterShift();
    });
  }

  private measureSideSlotWidth(startElement?: HTMLElement, endElement?: HTMLElement, fallbackWidth: number = 0): number {
    const startWidth = startElement?.getBoundingClientRect().width || 0;
    const endWidth = endElement?.getBoundingClientRect().width || 0;
    return Math.max(fallbackWidth, Math.ceil(startWidth), Math.ceil(endWidth));
  }

  private recalculateExpandedComposerGeometry(shellElement: HTMLElement, shellWidth: number): void {
    const expandedStartWidth = this.measureSingleWidth(this.expandedStartRef?.nativeElement, this.lastExpandedStartWidth);
    const expandedEndWidth = this.measureSingleWidth(this.expandedEndRef?.nativeElement, this.lastExpandedEndWidth);

    this.lastExpandedStartWidth = expandedStartWidth;
    this.lastExpandedEndWidth = expandedEndWidth;

    const expandedGap = shellWidth <= 640 ? 10 : 14;
    const expandedBasePadding = shellWidth <= 640 ? 16 : 24;
    const maxExpandedMainWidth = 600;
    const leftEnvelope = expandedStartWidth + expandedGap + expandedBasePadding;
    const rightEnvelope = expandedEndWidth + expandedGap + expandedBasePadding;
    const sideEnvelope = Math.max(leftEnvelope, rightEnvelope);
    const expandedMainWidth = Math.max(180, Math.min(maxExpandedMainWidth, Math.floor(shellWidth - (sideEnvelope * 2))));
    const opticalShift = this.calculateExpandedOpticalShift(expandedStartWidth, expandedEndWidth, shellWidth);
    const mainLeft = Math.round((shellWidth - expandedMainWidth) / 2) - opticalShift;
    const mainRight = mainLeft + expandedMainWidth;
    const expandedStartLeft = mainLeft - expandedGap - expandedStartWidth;
    const expandedEndLeft = mainRight + expandedGap;
    const expandedBaseLeft = expandedStartLeft - expandedBasePadding;
    const expandedBaseWidth = Math.round(
      expandedMainWidth
      + (expandedGap * 2)
      + expandedStartWidth
      + expandedEndWidth
      + (expandedBasePadding * 2)
    );

    shellElement.style.setProperty('--advanced-search-expanded-main-width', `${expandedMainWidth}px`);
    shellElement.style.setProperty('--advanced-search-expanded-start-left', `${Math.round(expandedStartLeft)}px`);
    shellElement.style.setProperty('--advanced-search-expanded-end-left', `${Math.round(expandedEndLeft)}px`);
    shellElement.style.setProperty('--advanced-search-expanded-base-left', `${Math.round(expandedBaseLeft)}px`);
    shellElement.style.setProperty('--advanced-search-expanded-base-width', `${expandedBaseWidth}px`);
  }

  private calculateExpandedOpticalShift(startWidth: number, endWidth: number, shellWidth: number): number {
    const sideDelta = Math.max(0, endWidth - startWidth);
    const maxOpticalShift = shellWidth <= 640 ? 6 : 10;
    return Math.round(Math.min(maxOpticalShift, sideDelta / 7));
  }

  private measureSingleWidth(element: HTMLElement | undefined, fallbackWidth: number): number {
    const measuredWidth = element?.getBoundingClientRect().width || 0;
    return Math.max(fallbackWidth, Math.ceil(measuredWidth));
  }

  private calculateAvailableMainWidth(
    rowElement: HTMLElement | undefined,
    rowWidth: number,
    sideSlotWidth: number,
    fallbackPaddingX: number,
    fallbackColumnGap: number
  ): number {
    const styles = rowElement ? window.getComputedStyle(rowElement) : undefined;
    const paddingX = styles
      ? (parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0'))
      : fallbackPaddingX;
    const columnGap = styles ? parseFloat(styles.columnGap || '0') : fallbackColumnGap;

    return rowWidth - paddingX - (sideSlotWidth * 2) - (columnGap * 2);
  }

  private appendSelectedFiles(selectedFiles: File[]): number {
    if (!this.attachmentsEnabled) {
      return 0;
    }

    if (selectedFiles.length === 0) {
      return 0;
    }

    const imageFiles = selectedFiles.filter((file) => file.type.startsWith('image/'));
    const rejectedFilesCount = selectedFiles.length - imageFiles.length;

    if (rejectedFilesCount > 0) {
      this.utilityService.showSnackBar('Only image files are supported.', 0);
    }

    const remainingSlots = this.maxAttachments - this.attachments.length;
    const filesToAppend = imageFiles.slice(0, remainingSlots);

    if (imageFiles.length > remainingSlots) {
      this.utilityService.showSnackBar(`You can attach up to ${this.maxAttachments} images.`, 0);
    }

    if (filesToAppend.length === 0) {
      return 0;
    }

    this.attachments = [
      ...this.attachments,
      ...filesToAppend.map((file) => this.createAttachment(file))
    ];

    this.emitDraftChange();
    return filesToAppend.length;
  }

  private createAttachment(file: File): AdvancedSearchAttachment {
    return {
      id: `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      file,
      previewUrl: URL.createObjectURL(file)
    };
  }

  private emitDraftChange(): void {
    this.draftChange.emit(this.buildDraft());
  }

  private resetDraft(): void {
    this.prompt = '';
    this.attachments.forEach((attachment) => {
      URL.revokeObjectURL(attachment.previewUrl);
    });
    this.attachments = [];
    this.resetFileInput();
    this.resizeTextarea();
  }

  private buildDraft(): AdvancedSearchDraft {
    return {
      prompt: this.prompt,
      attachments: this.attachments.map((attachment) => ({
        id: attachment.id,
        file: attachment.file,
        previewUrl: attachment.previewUrl
      }))
    };
  }

  private getPromptPlaceholder(): string {
    if (!this.attachmentsEnabled) {
      return this.placeholder || 'Describe a scene, model';
    }

    return this.attachments.length > 0 ? 'Describe or edit an image' : (this.placeholder || 'Describe a scene, model');
  }

  private resetFileInput(): void {
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();

    if (this.recalculateFooterShiftFrame && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.recalculateFooterShiftFrame);
    }

    this.attachments.forEach((attachment) => {
      URL.revokeObjectURL(attachment.previewUrl);
    });
  }
}
